import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { Badge, Button, Modal } from '@lipaflow/ui'
import { adminService, type CurriculumDocument, type CurriculumEdition } from '../../services/admin'
import { DOCUMENT_STATUS, EDITION_STATUS, field, labelCls } from './labels'
import { apiErrorMessage } from '@lipaflow/shared'

function useRefresh() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['curriculum-v2'] })
}

/** The AI model is an expert override; most runs use the default. */
function AdvancedModel({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <details>
      <summary className="cursor-pointer text-caption text-ink-muted">Advanced</summary>
      <label className="block mt-2">
        <span className={labelCls}>AI model (leave empty for the default)</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} className={clsx(field, 'font-mono')} />
      </label>
    </details>
  )
}

type EditionMode = 'view' | 'extract' | 'batch' | 'prerequisites' | 'activate' | 'edit' | 'remove'

/**
 * One edition and the pipeline steps that act on it, in order: extract
 * documents into it, work out prerequisites, then make it live.
 */
export function EditionPanel({
  edition,
  documents,
  editions,
  onClose,
}: {
  edition: CurriculumEdition | null
  documents: CurriculumDocument[]
  editions: CurriculumEdition[]
  onClose: () => void
}) {
  const refresh = useRefresh()
  const [mode, setMode] = useState<EditionMode>('view')
  const [documentId, setDocumentId] = useState('')
  const [subject, setSubject] = useState('')
  const [model, setModel] = useState('')
  const [batch, setBatch] = useState<Record<string, string>>({})
  const [target, setTarget] = useState('')
  const [edit, setEdit] = useState({ title: '', version_tag: '' })

  const reset = () => { setMode('view'); setDocumentId(''); setSubject(''); setModel(''); setBatch({}); setTarget('') }
  const close = () => { reset(); onClose() }

  const extract = useMutation({
    mutationFn: () => adminService.runCurriculumV2Extraction({
      document_id: documentId, edition_id: edition!.id, subject_hint: subject.trim() || null, model_name: model.trim() || null,
    }),
    onSuccess: (r) => { toast.success(`Extracted ${r.learning_outcomes} learning outcomes`); refresh(); reset() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't extract that document")),
  })
  const runBatch = useMutation({
    mutationFn: () => adminService.runCurriculumV2BatchExtraction({
      items: Object.entries(batch).map(([id, hint]) => ({ document_id: id, edition_id: edition!.id, subject_hint: hint.trim() || null })),
      model_name: model.trim() || null,
    }),
    onSuccess: (r) => { toast.success(`${r.succeeded} extracted${r.failed ? `, ${r.failed} failed` : ''}`); refresh(); reset() },
    onError: (e) => toast.error(apiErrorMessage(e, 'The batch failed')),
  })
  const prerequisites = useMutation({
    mutationFn: () => adminService.inferCurriculumV2Prerequisites({
      edition_id: edition!.id, target_edition_id: target || null, model_name: model.trim() || null,
    }),
    onSuccess: (r) => { toast.success(`${r.edges_created} links added, ${r.edges_skipped} skipped`); reset() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't work out prerequisites")),
  })
  const activate = useMutation({
    mutationFn: () => adminService.activateCurriculumV2Edition(edition!.id),
    onSuccess: (r) => { toast.success(`Live: ${r.outcomes} outcomes, ${r.embeddings_created} indexed for search`); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't make it live")),
  })
  const save = useMutation({
    mutationFn: () => adminService.updateCurriculumV2Edition(edition!.id, {
      title: edit.title.trim() || undefined, version_tag: edit.version_tag.trim() || undefined,
    }),
    onSuccess: () => { toast.success('Saved'); refresh(); reset() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't save")),
  })
  const remove = useMutation({
    mutationFn: () => adminService.deleteCurriculumV2Edition(edition!.id),
    onSuccess: () => { toast.success('Edition retired'); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't remove it")),
  })

  if (!edition) return <Modal open={false} onClose={close}>{null}</Modal>
  const status = EDITION_STATUS[edition.status] ?? { label: edition.status, variant: 'neutral' as const }
  const isDraft = edition.status === 'draft'
  const usable = documents.filter((d) => d.status === 'uploaded')

  return (
    <Modal open onClose={close} title={edition.title || edition.version_tag} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant}>{status.label}</Badge>
          <span className="text-body-sm text-ink-muted font-mono">{edition.curriculum_code} · {edition.version_tag}</span>
          {edition.published_year && <span className="text-body-sm text-ink-muted">· {edition.published_year}</span>}
        </div>

        {mode === 'view' && (
          <div className="space-y-2">
            {isDraft && (
              <ol className="space-y-2">
                <Step n={1} title="Extract documents into this edition" hint="Reads a curriculum PDF and pulls out strands, sub-strands and learning outcomes.">
                  <Button size="sm" onClick={() => setMode('extract')} disabled={usable.length === 0}>Extract one</Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('batch')} disabled={usable.length < 2}>Extract several</Button>
                </Step>
                <Step n={2} title="Work out prerequisites" hint="Links outcomes that build on each other, so lessons follow the right order.">
                  <Button size="sm" variant="secondary" onClick={() => setMode('prerequisites')}>Work them out</Button>
                </Step>
                <Step n={3} title="Check it in the curriculum library" hint="Read through what was extracted before teachers see it.">
                  <Link to="../curriculum-library" className="text-body-sm text-accent hover:underline">Open the library</Link>
                </Step>
                <Step n={4} title="Make it live" hint="Teachers can use it for planning and question banks.">
                  <Button size="sm" variant="secondary" onClick={() => setMode('activate')}>Make live</Button>
                </Step>
              </ol>
            )}
            {!isDraft && (
              <p className="text-body-sm text-ink-muted">
                {edition.status === 'active' ? 'This edition is live for teachers.' : 'This edition has been retired.'}
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
              {edition.status === 'active' && (
                <Button size="sm" variant="secondary" onClick={() => setMode('prerequisites')}>Work out prerequisites again</Button>
              )}
              {isDraft && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit({ title: edition.title, version_tag: edition.version_tag }); setMode('edit') }}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('remove')}>Remove</Button>
                </>
              )}
            </div>
          </div>
        )}

        {mode === 'extract' && (
          <Section onBack={reset} action={<Button onClick={() => extract.mutate()} disabled={!documentId} isLoading={extract.isPending}>Extract</Button>}>
            <label className="block">
              <span className={labelCls}>Document</span>
              <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} className={field} aria-label="Document">
                <option value="">Choose a document…</option>
                {usable.map((d) => <option key={d.id} value={d.id}>{d.title} ({d.curriculum_code})</option>)}
              </select>
            </label>
            <label className="block">
              <span className={labelCls}>Subject (optional, helps the AI)</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Social Studies" className={field} />
            </label>
            <AdvancedModel value={model} onChange={setModel} />
            <p className="text-caption text-ink-muted">This can take a minute for a long document.</p>
          </Section>
        )}

        {mode === 'batch' && (
          <Section
            onBack={reset}
            action={<Button onClick={() => runBatch.mutate()} disabled={Object.keys(batch).length === 0} isLoading={runBatch.isPending}>Extract {Object.keys(batch).length || ''}</Button>}
          >
            <p className="text-body-sm text-ink">Choose the documents to extract into this edition.</p>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {usable.map((d) => {
                const on = d.id in batch
                return (
                  <li key={d.id} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      aria-label={d.title}
                      checked={on}
                      onChange={() => setBatch((b) => {
                        const next = { ...b }
                        if (on) delete next[d.id]
                        else next[d.id] = ''
                        return next
                      })}
                    />
                    <span className="flex-1 text-body-sm text-ink">{d.title}</span>
                    {on && (
                      <input
                        aria-label={`Subject for ${d.title}`}
                        value={batch[d.id]}
                        onChange={(e) => setBatch((b) => ({ ...b, [d.id]: e.target.value }))}
                        placeholder="Subject (optional)"
                        className={clsx(field, 'max-w-[12rem]')}
                      />
                    )}
                  </li>
                )
              })}
            </ul>
            <AdvancedModel value={model} onChange={setModel} />
          </Section>
        )}

        {mode === 'prerequisites' && (
          <Section onBack={reset} action={<Button onClick={() => prerequisites.mutate()} isLoading={prerequisites.isPending}>Work them out</Button>}>
            <label className="block">
              <span className={labelCls}>Link to outcomes in</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className={field} aria-label="Link to outcomes in">
                <option value="">This same edition</option>
                {editions.filter((e) => e.id !== edition.id).map((e) => <option key={e.id} value={e.id}>{e.title || e.version_tag}</option>)}
              </select>
            </label>
            <AdvancedModel value={model} onChange={setModel} />
          </Section>
        )}

        {mode === 'activate' && (
          <Section
            onBack={reset}
            action={<Button onClick={() => activate.mutate()} isLoading={activate.isPending}>Make it live</Button>}
          >
            <p className="text-body-sm text-ink">
              Teachers will see this edition's learning outcomes in planning and question banks, and each outcome is
              indexed for search. It needs extracted outcomes first. Other live editions stay live.
            </p>
          </Section>
        )}

        {mode === 'edit' && (
          <Section onBack={reset} action={<Button onClick={() => save.mutate()} isLoading={save.isPending}>Save</Button>}>
            <label className="block"><span className={labelCls}>Title</span>
              <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={field} />
            </label>
            <label className="block"><span className={labelCls}>Version</span>
              <input value={edit.version_tag} onChange={(e) => setEdit({ ...edit, version_tag: e.target.value })} className={clsx(field, 'font-mono')} />
            </label>
          </Section>
        )}

        {mode === 'remove' && (
          <Section onBack={reset} action={<Button variant="secondary" onClick={() => remove.mutate()} isLoading={remove.isPending}>Remove edition</Button>}>
            <p className="text-body-sm text-ink">
              The edition is marked as retired and can't be made live. Anything already extracted into it is kept.
            </p>
          </Section>
        )}
      </div>
    </Modal>
  )
}

/** One source document: fix its details, or remove it if it hasn't been used. */
export function DocumentPanel({ document, onClose }: { document: CurriculumDocument | null; onClose: () => void }) {
  const refresh = useRefresh()
  const [mode, setMode] = useState<'view' | 'edit' | 'remove'>('view')
  const [form, setForm] = useState({ title: '', source_uri: '' })
  const close = () => { setMode('view'); onClose() }

  const save = useMutation({
    mutationFn: () => adminService.updateCurriculumV2Document(document!.id, {
      title: form.title.trim() || undefined, source_uri: form.source_uri.trim() || undefined,
    }),
    onSuccess: () => { toast.success('Saved'); refresh(); setMode('view') },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't save")),
  })
  const remove = useMutation({
    mutationFn: () => adminService.deleteCurriculumV2Document(document!.id),
    onSuccess: () => { toast.success('Document removed'); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't remove it")),
  })

  if (!document) return <Modal open={false} onClose={close}>{null}</Modal>
  const status = DOCUMENT_STATUS[document.status] ?? { label: document.status, variant: 'neutral' as const }
  const removable = document.status === 'uploaded'

  return (
    <Modal open onClose={close} title={document.title} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant}>{status.label}</Badge>
          <span className="text-body-sm text-ink-muted font-mono">{document.curriculum_code}</span>
          {document.is_global && <span className="text-body-sm text-ink-muted">· Shared with every school</span>}
        </div>
        <p className="text-caption text-ink-muted font-mono break-all">{document.source_uri}</p>

        {mode === 'view' && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
            <Button size="sm" variant="secondary" onClick={() => { setForm({ title: document.title, source_uri: document.source_uri }); setMode('edit') }}>Edit</Button>
            {removable && <Button size="sm" variant="ghost" onClick={() => setMode('remove')}>Remove</Button>}
          </div>
        )}
        {mode === 'edit' && (
          <Section onBack={() => setMode('view')} action={<Button onClick={() => save.mutate()} isLoading={save.isPending}>Save</Button>}>
            <label className="block"><span className={labelCls}>Title</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
            </label>
            <label className="block"><span className={labelCls}>File location</span>
              <input value={form.source_uri} onChange={(e) => setForm({ ...form, source_uri: e.target.value })} className={clsx(field, 'font-mono')} />
            </label>
          </Section>
        )}
        {mode === 'remove' && (
          <Section onBack={() => setMode('view')} action={<Button variant="secondary" onClick={() => remove.mutate()} isLoading={remove.isPending}>Remove document</Button>}>
            <p className="text-body-sm text-ink">It's marked as removed and can't be extracted. The file itself is kept.</p>
          </Section>
        )}
      </div>
    </Modal>
  )
}

export function AddDocumentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const refresh = useRefresh()
  const fileRef = useRef<HTMLInputElement>(null)
  const [form, setForm] = useState({ curriculum_code: 'CBC-KE', title: '', source_uri: '', is_global: true })

  const done = (title: string) => {
    toast.success(`Added: ${title}`)
    refresh()
    setForm((f) => ({ ...f, title: '', source_uri: '' }))
    if (fileRef.current) fileRef.current.value = ''
    onClose()
  }
  const upload = useMutation({
    mutationFn: (file: File) => adminService.uploadCurriculumV2Document(file, {
      curriculum_code: form.curriculum_code.trim(),
      title: form.title.trim() || file.name.replace(/\.pdf$/i, ''),
      is_global: form.is_global,
    }),
    onSuccess: (d) => done(d.title),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't upload the file")),
  })
  const fromUri = useMutation({
    mutationFn: () => adminService.createCurriculumV2Document({
      curriculum_code: form.curriculum_code.trim(), title: form.title.trim(), source_uri: form.source_uri.trim(),
      is_global: form.is_global, checksum: null,
    }),
    onSuccess: (d) => done(d.title),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't add the document")),
  })

  return (
    <Modal open={open} onClose={onClose} title="Add a curriculum document">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Curriculum</span>
            <input value={form.curriculum_code} onChange={(e) => setForm({ ...form, curriculum_code: e.target.value })} className={clsx(field, 'font-mono')} />
          </label>
          <label className="block"><span className={labelCls}>Title (optional for uploads)</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Grade 8 Social Studies" className={field} />
          </label>
        </div>
        <label className="inline-flex items-center gap-2 text-body-sm text-ink">
          <input type="checkbox" checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })} />
          Share with every school
        </label>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          aria-label="PDF file"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f && form.curriculum_code.trim()) upload.mutate(f) }}
        />
        <Button
          className="w-full"
          leftIcon={<ArrowUpTrayIcon className="h-5 w-5" />}
          onClick={() => fileRef.current?.click()}
          disabled={!form.curriculum_code.trim()}
          isLoading={upload.isPending}
        >
          Upload a PDF
        </Button>
        <details>
          <summary className="cursor-pointer text-caption text-ink-muted">Or use a file already in cloud storage</summary>
          <div className="mt-2 space-y-2">
            <input value={form.source_uri} onChange={(e) => setForm({ ...form, source_uri: e.target.value })} placeholder="gs://lipaflow-curriculum/…" className={clsx(field, 'font-mono')} aria-label="File location" />
            <Button
              variant="secondary"
              onClick={() => fromUri.mutate()}
              disabled={!form.source_uri.trim() || !form.title.trim() || !form.curriculum_code.trim()}
              isLoading={fromUri.isPending}
            >
              Add from storage
            </Button>
          </div>
        </details>
      </div>
    </Modal>
  )
}

export function NewEditionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const refresh = useRefresh()
  const [form, setForm] = useState({ curriculum_code: 'CBC-KE', version_tag: '', title: '', published_year: '', locale: 'en' })
  const create = useMutation({
    mutationFn: () => adminService.createCurriculumV2Edition({
      curriculum_code: form.curriculum_code.trim(), version_tag: form.version_tag.trim(), title: form.title.trim(),
      published_year: form.published_year ? Number(form.published_year) : null, locale: form.locale.trim() || 'en',
    }),
    onSuccess: (e) => {
      toast.success(`Draft edition created: ${e.title || e.version_tag}`)
      refresh()
      setForm((f) => ({ ...f, version_tag: '', title: '', published_year: '' }))
      onClose()
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't create the edition")),
  })
  const ok = form.curriculum_code.trim() && form.version_tag.trim() && form.title.trim()

  return (
    <Modal open={open} onClose={onClose} title="New curriculum edition">
      <div className="space-y-4">
        <p className="text-body-sm text-ink-muted">An edition is one release of a curriculum. It starts as a draft: extract documents into it, check it, then make it live.</p>
        <label className="block"><span className={labelCls}>Title</span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Kenya CBC Junior School 2024 revision" className={field} />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Curriculum</span>
            <input value={form.curriculum_code} onChange={(e) => setForm({ ...form, curriculum_code: e.target.value })} className={clsx(field, 'font-mono')} />
          </label>
          <label className="block"><span className={labelCls}>Version</span>
            <input value={form.version_tag} onChange={(e) => setForm({ ...form, version_tag: e.target.value })} placeholder="e.g. 2024-rev1" className={clsx(field, 'font-mono')} />
          </label>
          <label className="block"><span className={labelCls}>Year published (optional)</span>
            <input inputMode="numeric" value={form.published_year} onChange={(e) => setForm({ ...form, published_year: e.target.value })} className={field} />
          </label>
          <label className="block"><span className={labelCls}>Language</span>
            <input value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} className={clsx(field, 'font-mono')} />
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!ok} isLoading={create.isPending}>Create draft edition</Button>
        </div>
      </div>
    </Modal>
  )
}

function Step({ n, title, hint, children }: { n: number; title: string; hint: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3 p-3 rounded-card border border-hairline">
      <span className="h-6 w-6 shrink-0 rounded-full bg-accent-soft text-accent text-caption font-semibold flex items-center justify-center">{n}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-body-sm font-medium text-ink">{title}</span>
        <span className="block text-caption text-ink-muted">{hint}</span>
        <span className="mt-2 flex flex-wrap items-center gap-2">{children}</span>
      </span>
    </li>
  )
}

function Section({ children, action, onBack }: { children: React.ReactNode; action: React.ReactNode; onBack: () => void }) {
  return (
    <div className="space-y-3">
      {children}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        {action}
      </div>
    </div>
  )
}
